'use client';

import { usePathname } from 'next/navigation';
import { PropsWithChildren, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { AutoRegistrationModal } from '@/components/auth';
import { EmailConfirmPopup } from '@/components/EmailConfirm';
import { GlobalTooltip } from '@/components/GlobalTooltip';
import { MobileAppPopup } from '@/components/MobileAppPopup/components';
import { PaymentNotification } from '@/components/PaymentNotification';
import GeneralSkeleton from '@/components/skeletons/GeneralSkeleton';
import LobbyPageSkeleton from '@/components/skeletons/LobbyPageSkeleton';
import { UserSessionTimer } from '@/components/UserSessionTimer';
import { useBonusCode } from '@/hooks/bonusCode';
import { useComparingLanguagesWithUserCountry } from '@/hooks/comparingLanguages';
import { useCookiesModal } from '@/hooks/cookies';
import useEstonia from '@/hooks/estonia';
import { useJackpotWinnerModal } from '@/hooks/jackpotWinner';
import { useKnowYourLimit } from '@/hooks/knowYourLimit';
import { useLicenseScript } from '@/hooks/licenseScript';
import { useNetworksModal } from '@/hooks/networks';
import { useServiceWorker } from '@/hooks/serviceWorker';
import { useLootboxModal } from '@/hooks/useLootboxModal';
import { useMobileAppBanner } from '@/hooks/useMobileAppBanner';
import { usePopupHeight } from '@/hooks/usePopupHeight';
import { useTermsAndConditionsAcceptanceModal } from '@/hooks/useTermsAndConditionsAcceptanceModal';
import { useTrackingUserParamsHook } from '@/modules/userTrackingParams';
import { useCommonStore } from '@/store';

import { ScClientLayoutContainer } from './styled';

interface ClientCommonLayoutProps {
  locale: string;
}

export const ClientCommonLayout = ({
  locale,
  children,
}: PropsWithChildren<ClientCommonLayoutProps>) => {
  const {
    isAuthenticated,
    isEmailConfirmationPopup,
    isMobileDevice,
    pendingStore,
  } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      isEmailConfirmationPopup: state.isEmailConfirmationPopup,
      isMobileDevice: state.isMobileDevice,
      pendingStore: state.pendingStore,
    }))
  );

  const jackpotWonModal = useJackpotWinnerModal();
  const checkLocaleModal = useComparingLanguagesWithUserCountry(locale);
  const knowYourLimit = useKnowYourLimit();
  const networksModal = useNetworksModal();
  const cookiesModal = useCookiesModal();
  const lootboxModal = useLootboxModal();
  const { isEstonia } = useEstonia();

  useTrackingUserParamsHook();
  useBonusCode();
  useLicenseScript();
  // useConfirmRegistrationModal();
  useTermsAndConditionsAcceptanceModal();

  useServiceWorker();

  const { isShowMobileAppBanner } = useMobileAppBanner();
  const { popupHeight } = usePopupHeight();

  const isSessionTimer = useMemo(
    () => isAuthenticated && isEstonia && !isMobileDevice,
    [isAuthenticated, isEstonia, isMobileDevice]
  );

  const pathname = usePathname();

  if (pendingStore) {
    if (
      pathname === '/' ||
      pathname === `/${locale}` ||
      pathname.includes('/games')
    )
      return <LobbyPageSkeleton withTitle />;

    return <GeneralSkeleton />;
  }

  return (
    <ScClientLayoutContainer $popupHeight={popupHeight}>
      {isEmailConfirmationPopup && <EmailConfirmPopup />}
      {isShowMobileAppBanner && <MobileAppPopup />}

      {children}

      <PaymentNotification />
      {cookiesModal}
      {knowYourLimit}
      {jackpotWonModal}
      {checkLocaleModal}
      {networksModal}
      {lootboxModal}

      {isSessionTimer && <UserSessionTimer />}

      <AutoRegistrationModal />

      <GlobalTooltip />
    </ScClientLayoutContainer>
  );
};
